/**
 * SharedSkuPicker (BLU-711 Part B Stage 2 PR3).
 *
 * `ClientTargetPicker`-shaped FK picker for a shared-SKU election target:
 *  - REQUIRED with NO default — the parent gates its save action on
 *    `value !== null` (misrouting a stock election is the same blast radius
 *    as misrouting a receive, DESIGN.md §13.9).
 *  - NO free text — options come exclusively from the owner's nominated
 *    catalog (`inventory.listOwnerSharedCatalog`, server-side `is_shared`
 *    filter). The search box narrows; it can never mint.
 *  - Options already claimed by ANOTHER store (`mappedByOther`) render
 *    disabled with a visible label saying why (annotate-not-hide: hiding
 *    them would make the owner's catalog look smaller than it is).
 *  - ONE constant error string for a failed catalog load
 *    (`sharedStock.picker.loadError`) — covers the deny path
 *    (SHARED_CATALOG_FORBIDDEN) and transient failures alike, so the copy
 *    can never leak why access was refused.
 *
 * Search is SERVER-side (the catalog pages at 50): cmdk's own filter is off
 * (`shouldFilter={false}`) and the debounced input drives the query's
 * `search` param. Uses Popover+Command rather than Select because the option
 * universe routinely exceeds the 20-option Select ceiling (DESIGN.md §10).
 *
 * Phase 92 (UI-SPEC 1.2 step 1, D-20): `mode="garment"` picks one of a 3PL's
 * shared GARMENTS (a style, not a SKU row) for the client's supply composer.
 * One option per garment from `inventory.listOwnerSharedGarments` (searched,
 * counted and paged in one SQL call), or from an injected option source
 * (`useGarmentOptions`, D-21: the Upload Studio and the blank library feed it
 * later). Supply mode never shows "claimed by another store": many clients
 * share one blank (D-07), so nothing about another client appears.
 * `mode="sku"` (the default) is today's picker, unchanged.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Link2, Loader2, RefreshCw, Shirt } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusPill } from '@/components/shared/StatusPill';
import { cn } from '@/lib/utils';
import { trpc, type RouterOutputs } from '@/lib/trpc';

import type { OwnerSharedCatalogItem } from '../../lib/shared-stock';
import { GarmentThumb } from '../GarmentThumb';

const PAGE_LIMIT = 50;

const GARMENT_PAGE_LIMIT = 50;

export interface SharedSkuPickerSkuProps {
  /** Default. Today's back-up picker: one option per shared SKU row. */
  mode?: 'sku';
  /** Owner org whose nominated catalog supplies the options. */
  ownerOrganizationId: string;
  /** null = nothing chosen yet (required + no default; parent blocks save). */
  value: OwnerSharedCatalogItem | null;
  onChange: (item: OwnerSharedCatalogItem) => void;
  /** Default: true. Drives the required-state visuals + helper text. */
  required?: boolean;
  disabled?: boolean;
  /** Trigger id for label association. */
  id: string;
}

/** One of a 3PL's shared garments, as `inventory.listOwnerSharedGarments` returns it. */
export type SharedGarmentOption = RouterOutputs['inventory']['listOwnerSharedGarments']['items'][number];

/** What a garment option source hands the picker for one search. */
export interface GarmentOptionsResult {
  items: SharedGarmentOption[];
  /** Garments matching the search across every page. */
  total: number;
  /** False when the 3PL has not turned its blanks on for the caller (D-08). */
  blanksAllowed: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * A garment option source (D-21). Called as a hook, so it must be the same
 * function for the life of the picker. `enabled` stays false until the
 * popover first opens, so a closed picker reads nothing.
 */
export type UseGarmentOptions = (args: {
  ownerOrganizationId: string;
  search: string;
  enabled: boolean;
}) => GarmentOptionsResult;

/**
 * The default garment source: one `listOwnerSharedGarments` page per open
 * popover search (300 ms debounce upstream, `staleTime` 30 s,
 * `keepPreviousData`; UI-SPEC performance budget).
 */
const useOwnerSharedGarmentOptions: UseGarmentOptions = ({ ownerOrganizationId, search, enabled }) => {
  const query = trpc.inventory.listOwnerSharedGarments.useQuery(
    { ownerOrganizationId, search: search || undefined, limit: GARMENT_PAGE_LIMIT },
    { enabled, placeholderData: keepPreviousData, staleTime: 30_000, retry: 1 },
  );
  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    blanksAllowed: query.data?.blanksAllowed ?? true,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
};

export interface SharedSkuPickerGarmentProps {
  mode: 'garment';
  /** The 3PL whose shared garments are offered. */
  ownerOrganizationId: string;
  /** The 3PL's name, for the copy ("Search {owner}'s blanks"). */
  ownerName: string;
  /** null = nothing chosen yet (required + no default). */
  value: SharedGarmentOption | null;
  onGarmentChange: (garment: SharedGarmentOption) => void;
  /** The visible label (the composer's step label). */
  label: string;
  /** Default: true. Drives the required-state visuals + helper text. */
  required?: boolean;
  disabled?: boolean;
  /** Trigger id for label association. */
  id: string;
  /** Injected option source (D-21); defaults to `listOwnerSharedGarments`. */
  useGarmentOptions?: UseGarmentOptions;
  /** The trigger's words with nothing chosen (default "Choose a blank"). */
  placeholder?: string;
}

export type SharedSkuPickerProps = SharedSkuPickerSkuProps | SharedSkuPickerGarmentProps;

export function SharedSkuPicker(props: SharedSkuPickerProps) {
  return props.mode === 'garment' ? <GarmentModePicker {...props} /> : <SkuModePicker {...props} />;
}

function SkuModePicker({
  ownerOrganizationId,
  value,
  onChange,
  required = true,
  disabled = false,
  id,
}: SharedSkuPickerSkuProps) {
  const { t } = useTranslation('productSync');
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const catalogQuery = trpc.inventory.listOwnerSharedCatalog.useQuery(
    {
      ownerOrganizationId,
      search: debouncedSearch || undefined,
      limit: PAGE_LIMIT,
    },
    { enabled: open, placeholderData: keepPreviousData, staleTime: 30_000, retry: 1 },
  );

  const items = useMemo(() => catalogQuery.data?.items ?? [], [catalogQuery.data]);
  const hasMore = catalogQuery.data?.hasMore ?? false;
  const isMissing = required && !value && !disabled;

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-sm">
        {t('sharedStock.picker.label')}
        {required && (
          <span aria-hidden="true" className="text-destructive ml-0.5">
            *
          </span>
        )}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-required={required}
            aria-invalid={isMissing}
            aria-describedby={isMissing ? `${id}-helper` : undefined}
            disabled={disabled}
            className={cn(
              'h-8 w-full max-w-sm justify-between px-3 text-xs font-normal transition-colors',
              isMissing && 'border-amber-300 ring-1 ring-amber-100/50 focus-visible:ring-amber-200',
            )}
          >
            {value ? (
              <span className="flex min-w-0 items-center gap-2">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                <span className="truncate font-mono font-medium">{value.sku}</span>
                {value.title && <span className="truncate text-muted-foreground">{value.title}</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('sharedStock.picker.placeholder')}</span>
            )}
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder={t('sharedStock.picker.searchPlaceholder')}
            />
            <CommandList>
              {catalogQuery.isError ? (
                // ONE constant string, never the raw error message.
                <p role="alert" className="px-3 py-6 text-center text-xs text-destructive">
                  {t('sharedStock.picker.loadError')}
                </p>
              ) : catalogQuery.isLoading ? (
                <p className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {t('sharedStock.picker.loading')}
                </p>
              ) : (
                <>
                  <CommandEmpty className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {t('sharedStock.picker.empty')}
                  </CommandEmpty>
                  <CommandGroup>
                    {items.map((item) => {
                      const claimed = item.mappedByOther;
                      const selected = value?.id === item.id;
                      return (
                        <CommandItem
                          key={item.id}
                          value={item.id}
                          disabled={claimed}
                          onSelect={() => {
                            if (claimed) return;
                            onChange(item);
                            setOpen(false);
                          }}
                          className="gap-2"
                        >
                          <Check
                            className={cn('h-3.5 w-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                            aria-hidden="true"
                          />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate font-mono text-xs font-medium">{item.sku}</span>
                              {item.mappedByCaller && (
                                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border">
                                  {t('sharedStock.picker.mappedByYou')}
                                </span>
                              )}
                            </span>
                            {/* PR #1667 review: `mappedByCaller` is code-blind
                                — re-electing this SKU for a DIFFERENT blank
                                code is a guaranteed conflict, so the badge
                                carries the caveat until the read model can say
                                which code the existing mapping serves. */}
                            {item.mappedByCaller && !claimed && (
                              <span className="text-[11px] text-muted-foreground">
                                {t('sharedStock.picker.mappedByYouCaveat')}
                              </span>
                            )}
                            <span className="truncate text-[11px] text-muted-foreground">
                              {[item.title, item.size, item.color].filter(Boolean).join(' · ') || '-'}
                            </span>
                            {claimed && (
                              <span className="text-[11px] text-amber-700">
                                {t('sharedStock.picker.mappedByOther')}
                              </span>
                            )}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {hasMore && (
                    <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
                      {t('sharedStock.picker.hasMore')}
                    </p>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {isMissing && (
        <p id={`${id}-helper`} className="text-xs text-amber-700">
          {t('sharedStock.picker.helperRequired')}
        </p>
      )}
    </div>
  );
}

/** "{colours} colours · {sizes} sizes" on one line (UI-SPEC copy contract). */
function useGarmentMeta() {
  const { t } = useTranslation('productSync');
  return (garment: Pick<SharedGarmentOption, 'colourCount' | 'sizeCount'>) =>
    [
      t('supply.picker.metaColours', { count: garment.colourCount }),
      t('supply.picker.metaSizes', { count: garment.sizeCount }),
    ].join(' · ');
}

function GarmentModePicker({
  ownerOrganizationId,
  ownerName,
  value,
  onGarmentChange,
  label,
  required = true,
  disabled = false,
  id,
  useGarmentOptions = useOwnerSharedGarmentOptions,
  placeholder,
}: SharedSkuPickerGarmentProps) {
  const { t } = useTranslation('productSync');
  const meta = useGarmentMeta();
  const [open, setOpen] = useState(false);
  // Stays true after the first open, so a reopened popover shows the cached
  // page at once; nothing is read before the client opens the list.
  const [opened, setOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const options = useGarmentOptions({ ownerOrganizationId, search: debouncedSearch, enabled: opened });
  const isMissing = required && !value && !disabled;
  const query = debouncedSearch.trim();

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearch('');
  };

  let body: ReactNode;
  if (options.isError) {
    // ONE constant string whatever the cause (a refused read or a transient
    // failure), so the copy never says why access failed (T-92-26-03).
    body = (
      <div role="alert" className="flex flex-col items-center gap-2 px-3 py-6 text-center">
        <p className="text-xs leading-normal text-destructive">{t('supply.picker.loadError', { owner: ownerName })}</p>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none" onClick={options.refetch}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('supply.picker.retry')}
        </Button>
      </div>
    );
  } else if (options.isLoading) {
    body = (
      <p className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {t('supply.picker.loading')}
      </p>
    );
  } else if (!options.blanksAllowed) {
    // The 3PL turned its blanks off after the owner list loaded (D-08).
    body = (
      <p className="px-3 py-6 text-center text-xs leading-normal text-amber-800">
        {t('supply.consentOff', { owner: ownerName })}
      </p>
    );
  } else if (options.items.length === 0 && query !== '') {
    body = (
      <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
        <p className="text-xs leading-normal text-muted-foreground">{t('supply.picker.noMatch', { query })}</p>
        <Button variant="ghost" size="sm" className="h-8 px-3 text-xs motion-reduce:transition-none" onClick={clearSearch}>
          {t('supply.picker.clearSearch')}
        </Button>
      </div>
    );
  } else if (options.items.length === 0) {
    body = (
      <EmptyState
        size="sm"
        icon={Shirt}
        title={t('supply.picker.emptyTitle', { owner: ownerName })}
        description={t('supply.picker.emptyBody', { owner: ownerName })}
        className="px-3"
      />
    );
  } else {
    body = (
      <>
        <CommandGroup>
          {options.items.map((item) => {
            const selected = value?.garmentKey === item.garmentKey;
            return (
              <CommandItem
                key={item.garmentKey}
                value={item.garmentKey}
                title={item.title}
                onSelect={() => {
                  onGarmentChange(item);
                  setOpen(false);
                }}
                className="gap-2"
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0 text-threadheads-purple', selected ? 'opacity-100' : 'opacity-0')}
                  aria-hidden="true"
                />
                <GarmentThumb src={item.imageUrl} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-xs font-medium">{item.title}</span>
                  <span className="truncate text-xs tabular-nums text-muted-foreground">{meta(item)}</span>
                  {item.usedByCaller && (
                    <StatusPill tone="off" className="mt-1 w-fit max-w-full">
                      <span className="truncate">{t('supply.picker.usedByYou')}</span>
                    </StatusPill>
                  )}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>
        {options.total > options.items.length && (
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">{t('sharedStock.picker.hasMore')}</p>
        )}
      </>
    );
  }

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </Label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setOpened(true);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-required={required}
            aria-invalid={isMissing}
            aria-describedby={isMissing ? `${id}-helper` : undefined}
            disabled={disabled}
            title={value?.title}
            className={cn(
              'h-8 w-full max-w-sm justify-between gap-2 px-3 text-xs font-normal transition-colors duration-200 motion-reduce:transition-none',
              isMissing && 'border-amber-300 ring-1 ring-amber-100/50 focus-visible:ring-amber-200',
            )}
          >
            {value ? (
              <span className="flex min-w-0 items-center gap-2">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                <span className="truncate font-medium">{value.title}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{meta(value)}</span>
              </span>
            ) : (
              <span className="truncate text-muted-foreground">{placeholder ?? t('supply.picker.placeholder')}</span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder={t('supply.picker.search', { owner: ownerName })}
            />
            <CommandList>{body}</CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {isMissing && (
        <p id={`${id}-helper`} className="text-xs text-amber-700">
          {t('supply.picker.placeholder')}
        </p>
      )}
    </div>
  );
}
